// src/components/Profile.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Settings as SettingsIcon,
  Link as LinkIcon,
  Globe,
  Bell,
  Megaphone,
  LifeBuoy,
  CreditCard,
  Calendar as CalIcon,
  FileText,
  Volume2,
  VolumeX,
  Menu as MenuIcon,
  X,
} from "lucide-react";

import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Chip,
  Stack,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";

// ✅ language provider
import { useLang } from "../i18n/LanguageProvider";

// Import Sub-Components
import QuickLinks from "../mainframes/sidebar/QuickLinks";
import LanguageSetting from "../mainframes/sidebar/LanguageSetting";
import SupportTickets from "../mainframes/sidebar/SupportTickets";
import ReportScheduler from "../mainframes/sidebar/ReportScheduler";
import Announcements from "../mainframes/sidebar/Announcements";
import GeneralSetting from "../mainframes/sidebar/GeneralSettings";
import RechargePlans from "../mainframes/sidebar/RechargePlans";
import NotificationList from "../mainframes/sidebar/NotificationList";
import CalendarManager from "../mainframes/sidebar/CalendarManager";
import RenewalRequest from "../mainframes/sidebar/RenewalRequest";
import {
  isAppSoundEnabled,
  setAppSoundEnabled,
  subscribeToAppSoundPreference,
} from "../utils/appSound";

const DRAWER_WIDTH = 300;
const PROFILE_NAV_SIDEBAR_BG = "#152136";
const PROFILE_NAV_SIDEBAR_BORDER = "rgba(255,255,255,0.08)";
const PROFILE_NAV_TEXT = "rgba(255,255,255,0.92)";
const PROFILE_NAV_MUTED = "rgba(255,255,255,0.64)";
const PROFILE_NAV_HOVER = "rgba(255,255,255,0.08)";
const PROFILE_NAV_ACTIVE = "rgba(78,161,255,0.18)";
const PROFILE_NAV_ACTIVE_BORDER = "rgba(78,161,255,0.4)";

export default function Profile() {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useLang();

  // ✅ get user role from redux (same style you use elsewhere)
  const user = useSelector((state) =>
    state.auth?.user ||
    state.user?.data ||
    state.auth?.currentUser ||
    state.user?.currentUser ||
    state.auth?.userInfo
  );

  const roleRaw = user?.role || "";
  const role = String(roleRaw).trim().toLowerCase();
  const canSeeRenewalRequest = ["admin", "manager", "sub_admin", "group_admin"].includes(role);

  const [activeTab, setActiveTab] = useState("general");
  const [mobileOpen, setMobileOpen] = useState(false);

  // 🔊 Sound toggle
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => isAppSoundEnabled());

  const toggleSound = () => {
    setIsSoundEnabled(setAppSoundEnabled(!isSoundEnabled));
  };

  useEffect(() => subscribeToAppSoundPreference(setIsSoundEnabled), []);

  // ✅ ROLE RULES (as you said)
  const canSeeAll = useMemo(() => {
    // superadmin, manager, subadmin, dealer => all visible
    return ["admin", "manager", "sub_admin", "group_admin"].includes(role);
  }, [role]);

  const hideForUser = useMemo(() => {
    // user => hide plans
    return role === "user";
  }, [role]);

  const hideForViewer = useMemo(() => {
    // viewer => hide plans + reports
    return role === "viewer";
  }, [role]);

  const isFieldEmployee = useMemo(() => role === "field_emp", [role]);

  const baseMenuItems = useMemo(
    () => [
      { id: "general", label: t("GENERAL_SETTINGS", "General Settings"), icon: <SettingsIcon size={18} /> },
      { id: "links", label: t("QUICK_LINKS", "Quick Links"), icon: <LinkIcon size={18} /> },
      { id: "language", label: t("LANGUAGE_SETTINGS", "Language Settings"), icon: <Globe size={18} /> },
      { id: "notifications", label: t("NOTIFICATION_SETTINGS", "Notification Settings"), icon: <Bell size={18} /> },
      { id: "announcements", label: t("ANNOUNCEMENTS", "Announcements"), icon: <Megaphone size={18} /> },
      { id: "support", label: t("SUPPORT_TICKETS", "Support Tickets"), icon: <LifeBuoy size={18} /> },
      { id: "plans", label: t("RECHARGE_PLANS", "Recharge Plans"), icon: <CreditCard size={18} /> },
      { id: "calendar", label: t("CALENDAR", "Calendar"), icon: <CalIcon size={18} /> },
      { id: "renewalRequest", label: t("RENEWAL_REQUEST", "Renewal Request"), icon: <FileText size={18} /> },
      { id: "reports", label: t("REPORT_SCHEDULER", "Report Scheduler"), icon: <FileText size={18} /> },
    ],
    [t]
  );

  // ✅ FINAL menu items (filtered by role)
  const menuItems = useMemo(() => {
    let allowed = baseMenuItems;

    if (isFieldEmployee) {
      allowed = baseMenuItems.filter((m) => m.id === "general");
    } else if (hideForViewer) {
      allowed = baseMenuItems.filter((m) => !["plans", "reports", "wallet", "renewalRequest"].includes(m.id));
    } else if (hideForUser) {
      allowed = baseMenuItems.filter((m) => !["renewalRequest"].includes(m.id));
    } else if (!canSeeAll) {
      allowed = baseMenuItems;
    }

    if (!canSeeRenewalRequest) {
      allowed = allowed.filter((m) => m.id !== "renewalRequest");
    }

    return allowed;
  }, [baseMenuItems, canSeeAll, hideForUser, hideForViewer, isFieldEmployee, canSeeRenewalRequest]);

  // ✅ if active tab becomes hidden, fallback to "general"
  useEffect(() => {
    const exists = menuItems.some((m) => m.id === activeTab);
    if (!exists) setActiveTab("general");
  }, [menuItems, activeTab]);

  const activeLabel = useMemo(
    () =>
      menuItems.find((m) => m.id === activeTab)?.label ||
      t("ACCOUNT_SETTINGS", "Account Settings"),
    [activeTab, menuItems, t]
  );

  const renderContent = () => {
    switch (activeTab) {
      case "general":
        return <GeneralSetting />;
      case "links":
        return <QuickLinks />;
      case "language":
        return <LanguageSetting />;
      case "notifications":
        return <NotificationList />;
      case "announcements":
        return <Announcements />;
      case "support":
        return <SupportTickets />;
      case "calendar":
        return <CalendarManager />;
      case "renewalRequest":
        return <RenewalRequest />;
      case "reports":
        return <ReportScheduler />;
      case "plans":
        return <RechargePlans />;
      default:
        return (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 900 }}>
              {t("COMING_SOON", "Coming Soon…")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                "MODULE_FUTURE_UPDATE",
                "This module will be available in a future update."
              )}
            </Typography>
          </Box>
        );
    }
  };

  const handleTabClick = (id) => {
    setActiveTab(id);
    if (isMdDown) setMobileOpen(false);
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: PROFILE_NAV_SIDEBAR_BG,
        color: PROFILE_NAV_TEXT,
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          px: 2,
          py: 1.6,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${PROFILE_NAV_SIDEBAR_BORDER}`,
        }}
      >
        {isMdDown && (
          <IconButton onClick={() => setMobileOpen(false)} size="small" sx={{ color: PROFILE_NAV_TEXT }}>
            <X size={18} />
          </IconButton>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{ p: 1.2, flex: 1, overflow: "auto" }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const isActive = item.id === activeTab;
            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                sx={{
                  borderRadius: 2,
                  mb: 0.6,
                  color: PROFILE_NAV_TEXT,
                  bgcolor: isActive ? PROFILE_NAV_ACTIVE : "transparent",
                  border: `1px solid ${
                    isActive ? PROFILE_NAV_ACTIVE_BORDER : "transparent"
                  }`,
                  "&:hover": {
                    bgcolor: isActive ? PROFILE_NAV_ACTIVE : PROFILE_NAV_HOVER,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                    color: isActive ? "#8fc7ff" : PROFILE_NAV_MUTED,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontWeight: isActive ? 900 : 700,
                        color: isActive ? "#ffffff" : PROFILE_NAV_TEXT,
                      }}
                    >
                      {item.label}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>

        <Divider sx={{ my: 1.5, borderColor: PROFILE_NAV_SIDEBAR_BORDER }} />

        {/* Sound Control */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.2,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: "rgba(255,255,255,0.04)",
            borderColor: PROFILE_NAV_SIDEBAR_BORDER,
            color: PROFILE_NAV_TEXT,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: PROFILE_NAV_TEXT }}>
              {t("APP_SOUNDS", "App Sounds")}
            </Typography>
            <Typography variant="body2" sx={{ color: PROFILE_NAV_MUTED }} noWrap>
              {t("ALERTS_TONES", "Alerts & notification tones")}
            </Typography>
          </Box>

          <Tooltip
            title={
              isSoundEnabled
                ? t("DISABLE_SOUNDS", "Disable Sounds")
                : t("ENABLE_SOUNDS", "Enable Sounds")
            }
          >
            <Chip
              clickable
              onClick={toggleSound}
              icon={isSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              label={isSoundEnabled ? t("ON", "ON") : t("OFF", "OFF")}
              sx={{
                fontWeight: 900,
                borderRadius: 999,
                bgcolor: isSoundEnabled
                  ? alpha(theme.palette.success.main, 0.12)
                  : alpha(theme.palette.error.main, 0.10),
                color: isSoundEnabled
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                borderColor: isSoundEnabled
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.error.main, 0.25),
              }}
              variant="outlined"
            />
          </Tooltip>
        </Paper>
      </Box>

      {/* Drawer Footer */}
      <Box
        sx={{
          px: 2,
          py: 1.4,
          borderTop: `1px solid ${PROFILE_NAV_SIDEBAR_BORDER}`,
          bgcolor: "rgba(255,255,255,0.03)",
        }}
      >
        <Typography variant="caption" sx={{ color: PROFILE_NAV_MUTED }}>
          {t("TIP_SIDEBAR", "Tip: Use the sidebar to switch modules quickly.")}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, gap: 1 }}>
          {isMdDown && (
            <IconButton onClick={() => setMobileOpen(true)} size="small">
              <MenuIcon size={20} />
            </IconButton>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 900 }} noWrap>
              {activeLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {t("CONFIGURE_PREFS", "Configure your account preferences")}
            </Typography>
          </Box>

          {/* Sound Toggle in header */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                isSoundEnabled
                  ? t("DISABLE_SOUNDS", "Disable Sounds")
                  : t("ENABLE_SOUNDS", "Enable Sounds")
              }
            >
              <Chip
                clickable
                onClick={toggleSound}
                icon={isSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                label={`${t("SOUND", "Sound")}: ${isSoundEnabled ? t("ON", "ON") : t("OFF", "OFF")}`}
                variant="outlined"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  borderRadius: 999,
                  fontWeight: 900,
                }}
              />
            </Tooltip>

            <Tooltip
              title={
                isSoundEnabled
                  ? t("DISABLE_SOUNDS", "Disable Sounds")
                  : t("ENABLE_SOUNDS", "Enable Sounds")
              }
            >
              <IconButton
                onClick={toggleSound}
                size="small"
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                {isSoundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Layout */}
      <Box sx={{ display: "flex" }}>
        {/* Desktop Sidebar */}
        {!isMdDown && (
          <Box
            sx={{
              width: DRAWER_WIDTH,
              flex: "0 0 auto",
              borderRight: `1px solid ${PROFILE_NAV_SIDEBAR_BORDER}`,
              bgcolor: PROFILE_NAV_SIDEBAR_BG,
              minHeight: "calc(100vh - 72px)",
            }}
          >
            {drawerContent}
          </Box>
        )}

        {/* Mobile Drawer */}
        {isMdDown && (
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: DRAWER_WIDTH,
                maxWidth: "90vw",
                bgcolor: PROFILE_NAV_SIDEBAR_BG,
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, md: 2.5 },
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: { xs: 1.2, md: 2 },
              minHeight: "calc(100vh - 110px)",
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(10px)",
            }}
          >
            {renderContent()}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
